'use client';

/**
 * **Manage Estate Details — the matter's estate record, on the shared form
 * treatment** (VALE-892).
 *
 * A slide-over of the full estate record, committing **one field per blur** —
 * no Save, no Cancel, no whole-form submit. It renders the same
 * `AttributeSections` the Contacts drawer (VALE-863), the Inventory drawer and
 * the Overview's estate card render, at the same `DRAWER_SIZE.record`
 * width as the first two, from the same field builders in
 * `matter-attribute-specs.ts` the card reads. That is the whole point of the
 * conversion: this surface was the most divergent of the three Tom's 27 Aug
 * review looked at, and it is now not a separately-authored screen at all.
 *
 * ## What this host still owns
 *
 * · **The commit** — one `PUT` per field through `useUpdateMatter`, which must
 *   reject so the row can report it (`components/attributes/CLAUDE.md` §6).
 * · **The leave guard** — commit-on-blur does not mean nothing can be lost: a
 *   field held open by a refusal, a save still in flight, or an unsent part of
 *   the estate bank account all die with the drawer. `AttributeUnsavedStateProvider`
 *   reports the first two; `useUnsavedChangesGuard` stops the close (§6b).
 * · **Archive**, which moved from a footer button into the header beside the
 *   title — the treatment has no destination at the bottom of the form, and the
 *   Contacts drawer's Delete made the same move for the same reason.
 *
 * ## What the conversion removed, and why it is not a regression
 *
 * The react-hook-form body, its zod resolver, its render-phase `form.reset()`
 * matter-sync, its skeleton, and the submit handler's four hand-written
 * blank-to-null conversions are all gone. Each conversion now lives on the row
 * that owns the column (`decedentTextField`, `deathCertificateField`), where the
 * Overview card was already reading it — so the two surfaces cannot disagree
 * about whether an emptied Occupation clears the column or writes `""`.
 *
 * The submit handler's cache invalidation goes with it: `useUpdateMatter` owns
 * the matter's dependency graph (`getDependencies('matter', id)`), which is a
 * superset of the four keys this file used to invalidate by hand, and it runs on
 * every commit rather than once at the end.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Archive, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { SingleItemEditDrawer } from '@/components/shared/single-item-edit-drawer';
import {
  UnsavedChangesDialog,
  useUnsavedChangesGuard
} from '@/components/shared/unsaved-changes-guard';
import {
  ATTRIBUTE_DRAWER_CLOSE_OFFSET,
  ATTRIBUTE_DRAWER_HEADER_ROW,
  AttributeUnsavedStateProvider,
  focusOpenAttributeEditor
} from '@/components/attributes';
import { MatterEstateAttributeGrid } from '@/components/matters/matter-estate-attribute-grid';
import { useMatter, useUpdateMatter } from '@/lib/services/matters';
import type { UpdateMatterRequest } from '@/lib/services/matters/types';
import type { LawFirmUser } from '@/lib/services/account/types';
import { cn } from '@/lib/utils';

interface EditMatterDialogProps {
  matterId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fires once on close when at least one field was committed, so a host that
   * wants to react to "this matter changed" still can.
   *
   * It used to fire on the whole-form submit. There is no submit any more, and
   * firing it per commit would close the surface under a reader who has only
   * edited their first field — the CTA renderer wires it to *close the dialog*.
   */
  onSuccess?: () => void;
  /**
   * Delete the matter (VALE-1927). The host closes this drawer and opens the delete dialog
   * on the next frame: a confirm nested inside this Sheet hits the focus-scope bug
   * `ArchiveControls` documents.
   */
  onRequestDelete?: () => void;
  lawFirmUsers?: LawFirmUser[];
  usersLoading?: boolean;
}

/** The record's shape while it loads — four rows and a heading, twice over. */
const SKELETON_ROWS = ['a', 'b', 'c', 'd', 'e', 'f'] as const;

export function EditMatterDialog({
  matterId,
  open,
  onOpenChange,
  onSuccess,
  onRequestDelete,
  lawFirmUsers = [],
  usersLoading = false
}: EditMatterDialogProps) {
  const router = useRouter();
  const { data: matter, isLoading } = useMatter(matterId);
  /*
    **No toast on either outcome.** The grid reports a refusal inline and brings
    the field back with the typing intact, which is both more specific than a
    toast and attached to the row that failed; and a value that has visibly
    committed does not need announcing. The same call the Overview's estate card
    makes over these same rows.
  */
  const updateMatter = useUpdateMatter({ showToast: false });
  /*
    Archive is the one action here that is not a field, so it keeps its own
    mutation with toasts on — a failed archive has no row to report on.
  */
  const archiveMatter = useUpdateMatter();

  const [archiveConfirming, setArchiveConfirming] = useState(false);

  // Auto-reset the confirming state so a stale "click to confirm" affordance
  // doesn't sit indefinitely if the user wanders off.
  useEffect(() => {
    if (!archiveConfirming) return;
    const t = setTimeout(() => setArchiveConfirming(false), 5000);
    return () => clearTimeout(t);
  }, [archiveConfirming]);

  /** Whether anything was actually committed, so the close can say so. */
  const committedRef = useRef(false);

  /**
   * **State the attribute path could not store** (VALE-349) — a field held open
   * by a validation or server refusal, or a save still in flight. A ref because
   * it arrives *inside* the `pointerdown` that is closing the drawer.
   */
  const attributeUnsavedRef = useRef(false);
  /** Whether a field is open, so `Escape` reverts it rather than closing. */
  const editingFieldRef = useRef(false);

  const unsavedChanges = useUnsavedChangesGuard({
    enabled: true,
    resetKey: matterId,
    open,
    onOpenChange: (next) => {
      if (!next && committedRef.current) {
        committedRef.current = false;
        onSuccess?.();
      }
      onOpenChange(next);
    }
  });

  const { setIsDirty } = unsavedChanges;

  useEffect(() => {
    attributeUnsavedRef.current = false;
    editingFieldRef.current = false;
    committedRef.current = false;
  }, [matterId]);

  /**
   * **Rejects on failure, deliberately** (§6). `mutateAsync` rejects because
   * `extractMutationData` throws on a non-`success` result, and nothing here
   * catches it: a swallowed rejection is indistinguishable from a save that
   * worked.
   */
  const handleCommit = useCallback(
    async (patch: UpdateMatterRequest) => {
      await updateMatter.mutateAsync({ matterId, data: patch });
      committedRef.current = true;
    },
    [updateMatter, matterId]
  );

  /**
   * Forces the open field's commit before Radix's dismissable layer decides to
   * close (VALE-60) — the click that closes the drawer fires on `pointerdown`,
   * before the browser's own focus change, so without this a value typed into
   * an open editor is discarded silently.
   */
  const handlePointerDownOutside = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleArchive = async () => {
    const willArchive = !matter?.isArchived;
    try {
      await archiveMatter.mutateAsync({
        matterId,
        data: { isArchived: willArchive }
      });
      setArchiveConfirming(false);
      onOpenChange(false);
      onSuccess?.();
      // After archiving, the matter drops out of the active list and the user
      // is stranded on its now-hidden detail page. Send them back to the
      // all-matters view. Unarchive keeps them put — the matter is still
      // meaningful at its current URL.
      if (willArchive) {
        router.push('/all');
      }
    } catch (_error) {
      // Error toast is handled by the mutation hook
    }
  };

  return (
    <>
      <SingleItemEditDrawer
        open={open}
        onOpenChange={unsavedChanges.handleOpenChange}
        /*
          **`record` — the same width as the Contacts and Inventory drawers**
          (VALE-863). This surface capped itself at `sm:max-w-2xl`, which was the
          Sheet's own default rather than a measured choice. See
          `DRAWER_SIZE` in `single-item-edit-drawer.tsx`.
        */
        size="record"
        className={ATTRIBUTE_DRAWER_CLOSE_OFFSET}
        header={
          <div className={cn(ATTRIBUTE_DRAWER_HEADER_ROW, 'items-center')}>
            <SheetTitle>Manage Estate Details</SheetTitle>
            {matter && (
              <div className="flex shrink-0 items-center gap-1">
                {onRequestDelete && (
                  <Button
                    size="xs"
                    density="compact"
                    gap="standard"
                    type="button"
                    variant="ghost"
                    tone="muted"
                    onClick={onRequestDelete}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete matter
                  </Button>
                )}
                <ArchiveControls
                  isArchived={matter.isArchived}
                  confirming={archiveConfirming}
                  isPending={archiveMatter.isPending}
                  onPrimary={() => {
                    if (!archiveConfirming) {
                      setArchiveConfirming(true);
                      return;
                    }
                    handleArchive();
                  }}
                />
              </div>
            )}
          </div>
        }
        /*
          There is no input box at rest on a read-out surface, and the header
          holds a destructive action — so the container takes initial focus
          rather than the Archive button. Same call the Contacts drawer makes.
        */
        focusContainerOnOpen
        onEscapeKeyDown={(event) => {
          if (editingFieldRef.current) event.preventDefault();
        }}
        onPointerDownOutside={handlePointerDownOutside}
      >
        {isLoading || !matter ? (
          <div className="space-y-3">
            {SKELETON_ROWS.map((row) => (
              <Skeleton key={row} className="h-7 w-full" />
            ))}
          </div>
        ) : (
          /*
            Every grid on this surface reports its unsaved state here — one per
            `rows` block — and the union is what the leave guard reads (VALE-349).
          */
          <AttributeUnsavedStateProvider
            onUnsavedChange={(unsaved) => {
              attributeUnsavedRef.current = unsaved;
              setIsDirty(unsaved);
            }}
          >
            <MatterEstateAttributeGrid
              matter={matter}
              users={lawFirmUsers}
              onCommit={handleCommit}
              onEditingChange={(editing) => {
                editingFieldRef.current = editing;
              }}
            />
            {usersLoading && (
              <p className="text-muted-foreground text-xs">
                Loading your team…
              </p>
            )}
          </AttributeUnsavedStateProvider>
        )}
      </SingleItemEditDrawer>

      {/* Reachable when the treatment is holding a refusal or a save still in
          flight (VALE-349) — the only two things a commit-on-blur surface can
          still lose by closing. */}
      <UnsavedChangesDialog
        open={unsavedChanges.showUnsavedDialog}
        onOpenChange={(next) => {
          unsavedChanges.setShowUnsavedDialog(next);
          if (!next) focusOpenAttributeEditor();
        }}
        onDiscard={unsavedChanges.handleDiscardChanges}
        itemLabel="matter"
      />
    </>
  );
}

interface ArchiveControlsProps {
  isArchived: boolean;
  confirming: boolean;
  isPending: boolean;
  onPrimary: () => void;
}

/**
 * Archive, as a header action with an inline two-click confirm.
 *
 * The confirm is inline rather than a nested `AlertDialog`: nesting one inside
 * this Sheet hit a Radix focus-scope mismatch (radix-ui/primitives#3701) where
 * the dialog's action button stopped firing its `onClick` — visually it closed,
 * but archive never ran.
 */
function ArchiveControls({
  isArchived,
  confirming,
  isPending,
  onPrimary
}: ArchiveControlsProps) {
  const willArchive = !isArchived;
  const baseLabel = willArchive ? 'Archive matter' : 'Unarchive matter';
  const confirmLabel = willArchive
    ? 'Click again to archive'
    : 'Click again to unarchive';
  return (
    <Button
      size="xs"
      density="compact"
      gap="standard"
      type="button"
      variant={
        confirming
          ? willArchive
            ? 'destructive'
            : 'success'
          : willArchive
            ? 'ghost'
            : 'outline'
      }
      onClick={onPrimary}
      disabled={isPending}
      className={cn(!confirming && willArchive && 'text-muted-foreground')}
    >
      {isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Archive className="h-3.5 w-3.5" />
      )}
      {confirming ? confirmLabel : baseLabel}
    </Button>
  );
}
